import React from 'react';
import { Card, Tag, Typography, Tooltip } from 'antd';
import { Globe, AlertTriangle, CheckCircle } from 'lucide-react';
import { Handle, Position, NodeProps } from 'reactflow';

const { Text } = Typography;

interface HTTPRequestNodeData {
  label: string;
  description?: string;
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  responseFormat?: 'json' | 'text' | 'blob';
  timeout?: number;
  isExecuting?: boolean;
  isCompleted?: boolean;
  hasError?: boolean;
  onDelete?: () => void;
  isUnlinked?: boolean;
}

const HTTPRequestNode: React.FC<NodeProps<HTTPRequestNodeData>> = ({ 
  data, 
  selected 
}) => {
  const {
    label = 'HTTP Request',
    description,
    url,
    method = 'GET',
    responseFormat = 'json',
    timeout,
    isExecuting = false,
    isCompleted = false,
    hasError = false,
    isUnlinked = false
  } = data;

  const getNodeStyle = (): React.CSSProperties => {
    let baseStyle: React.CSSProperties = {
      minWidth: '200px',
      maxWidth: '280px',
      border: '2px solid',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      background: '#ffffff'
    };

    if (selected) {
      baseStyle.borderColor = '#1890ff';
      baseStyle.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
    } else if (isExecuting) {
      baseStyle.borderColor = '#faad14';
      baseStyle.boxShadow = '0 0 8px rgba(250, 173, 20, 0.3)';
    } else if (hasError) {
      baseStyle.borderColor = '#ff4d4f';
    } else if (isCompleted) {
      baseStyle.borderColor = '#52c41a';
    } else if (isUnlinked) {
      baseStyle.borderColor = '#ff4d4f';
      baseStyle.borderStyle = 'dashed';
    } else {
      baseStyle.borderColor = '#1890ff';
    }

    return baseStyle;
  };

  const getStatusIcon = () => {
    if (isExecuting) {
      return <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full" />;
    } else if (hasError) {
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    } else if (isCompleted) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return null;
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      'GET': 'green',
      'POST': 'blue',
      'PUT': 'orange',
      'DELETE': 'red',
      'PATCH': 'purple'
    };
    return colors[method] || 'default';
  };

  const truncateUrl = (url: string, maxLength: number = 35) => {
    if (!url) return 'No URL specified';
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  const extractDomain = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return domain;
    } catch {
      return 'Invalid URL';
    }
  };

  return (
    <div style={getNodeStyle()}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: '#1890ff',
          border: '2px solid #fff',
          width: '12px',
          height: '12px'
        }}
      />
      
      <Card size="small" variant="borderless" style={{ border: 'none' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Globe className="w-5 h-5 text-blue-600" />
            <Text strong style={{ fontSize: '14px' }}>{label}</Text>
          </div>
          {getStatusIcon()}
        </div>

        {/* Description */}
        {description && (
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
            {description}
          </Text>
        )}

        {/* Method Tag */}
        <div style={{ marginBottom: '8px' }}>
          <Tag 
            color={getMethodColor(method)}
            style={{ fontSize: '11px', fontWeight: 'bold' }}
          >
            {method}
          </Tag>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {extractDomain(url)}
          </Text>
        </div>

        {/* URL Preview */}
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '6px 8px', 
          borderRadius: '4px',
          marginBottom: '8px',
          fontSize: '11px',
          fontFamily: 'monospace',
          color: '#666',
          wordBreak: 'break-all'
        }}>
          <Tooltip title={url} placement="bottom">
            {truncateUrl(url)}
          </Tooltip>
        </div>

        {/* Configuration Tags */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <Tag color="blue" style={{ fontSize: '10px' }}>
            {responseFormat?.toUpperCase() || 'JSON'}
          </Tag>
          {timeout && (
            <Tag color="purple" style={{ fontSize: '10px' }}>
              {timeout}ms
            </Tag>
          )}
          {isUnlinked && (
            <Tag color="red" style={{ fontSize: '10px' }}>
              Unlinked
            </Tag>
          )}
        </div>

        {/* Execution Status */}
        {isExecuting && (
          <div style={{ 
            marginTop: '8px', 
            padding: '4px 8px', 
            backgroundColor: '#fff7e6',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#fa8c16'
          }}>
            Making request...
          </div>
        )}

        {hasError && (
          <div style={{ 
            marginTop: '8px', 
            padding: '4px 8px', 
            backgroundColor: '#fff2f0',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#ff4d4f'
          }}>
            Request failed
          </div>
        )}
      </Card>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: '#1890ff',
          border: '2px solid #fff',
          width: '12px',
          height: '12px'
        }}
      />
    </div>
  );
};

export default HTTPRequestNode;